import { useState } from "react";
import { FileText, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { ZoomableImage } from "@/components/ui/ZoomableImage";

interface TrailerBolPreviewDialogProps {
  trailerId: string;
  trailerLabel: string;
  docCount: number;
}

interface BolDoc {
  id: string;
  file_path: string;
  file_name: string;
  document_type: string;
  created_at: string;
  bucket: string;
  /** Where the document came from, shown as the row subtitle. */
  sourceLabel: string;
  event: {
    occurred_at: string;
    location: { short_code: string } | null;
  } | null;
}

export function TrailerBolPreviewDialog({ trailerId, trailerLabel, docCount }: TrailerBolPreviewDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: docs, isLoading } = useQuery({
    queryKey: ["trailer-bol-docs", trailerId],
    enabled: isOpen,
    queryFn: async () => {
      const results: BolDoc[] = [];

      // Get check-in events for this trailer
      const { data: events, error: evtErr } = await supabase
        .from("trailer_events")
        .select("id, occurred_at, location:locations!trailer_events_location_id_fkey(short_code)")
        .eq("trailer_id", trailerId)
        .eq("event_type", "checked_in")
        .order("occurred_at", { ascending: false });
      if (evtErr) throw evtErr;

      if (events?.length) {
        const eventIds = events.map((e) => e.id);
        const { data: documents, error: docErr } = await supabase
          .from("trailer_event_documents")
          .select("id, file_path, file_name, document_type, created_at, trailer_event_id")
          .in("trailer_event_id", eventIds)
          .order("created_at", { ascending: false });
        if (docErr) throw docErr;

        const eventMap = new Map(events.map((e) => [e.id, e]));
        for (const doc of documents ?? []) {
          const evt = eventMap.get(doc.trailer_event_id);
          const shortCode = (evt?.location as any)?.short_code ?? "Unknown location";
          results.push({
            id: doc.id,
            file_path: doc.file_path,
            file_name: doc.file_name,
            document_type: doc.document_type,
            created_at: doc.created_at,
            bucket: "idp-uploads",
            sourceLabel: `Check-in · ${shortCode}`,
            event: evt ? { occurred_at: evt.occurred_at, location: evt.location as any } : null,
          });
        }
      }

      // BoLs attached to orders that ran on this trailer (driver/dispatcher
      // uploads land on the order, not the trailer event).
      const { data: trailer } = await supabase
        .from("trailers")
        .select("trailer_id, tenant_id")
        .eq("id", trailerId)
        .maybeSingle();

      if (trailer?.trailer_id) {
        const { data: orders } = await supabase
          .from("orders")
          .select("id, unique_id")
          .eq("tenant_id", trailer.tenant_id)
          .eq("trailer_id", trailer.trailer_id);

        if (orders?.length) {
          const orderMap = new Map(orders.map((o) => [o.id, o.unique_id]));
          const { data: bols } = await supabase
            .from("order_bol_images")
            .select("id, order_id, image_path, image_url, created_at")
            .in("order_id", orders.map((o) => o.id))
            .order("created_at", { ascending: false });

          for (const bol of bols ?? []) {
            const path = bol.image_path || bol.image_url;
            if (!path) continue;
            results.push({
              id: bol.id,
              file_path: path,
              file_name: path.split("/").pop()?.replace(/^\d+-/, "") || "BOL",
              document_type: "bol",
              created_at: bol.created_at,
              bucket: "bol-images",
              sourceLabel: `Order ${orderMap.get(bol.order_id) ?? "—"}`,
              event: null,
            });
          }
        }
      }

      return results.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    },
  });

  const handleView = async (filePath: string, bucket: string) => {
    const { data } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  const handleDownload = async (filePath: string, fileName: string, bucket: string) => {
    const { data } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);
    if (!data?.signedUrl) return;
    const res = await fetch(data.signedUrl);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isImage = (path: string) => /\.(jpg|jpeg|png|gif|webp)$/i.test(path);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium hover:bg-muted/50 transition-colors"
      >
        <FileText className="h-3 w-3" />
        {docCount} BOL{docCount !== 1 ? "s" : ""}
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pr-8">
            <DialogTitle>BOL Documents — {trailerLabel}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto space-y-3 pt-2">
            {isLoading ? (
              Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-lg" />
              ))
            ) : docs?.length ? (
              docs.map((doc) => (
                <BolDocCard
                  key={doc.id}
                  doc={doc}
                  isImage={isImage(doc.file_path)}
                  onView={() => handleView(doc.file_path, doc.bucket)}
                  onDownload={() => handleDownload(doc.file_path, doc.file_name, doc.bucket)}
                />
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No documents found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BolDocCard({
  doc,
  isImage,
  onView,
  onDownload,
}: {
  doc: BolDoc;
  isImage: boolean;
  onView: () => void;
  onDownload: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadPreview = async () => {
    if (previewUrl || loadingPreview) return;
    setLoadingPreview(true);
    const { data } = await supabase.storage.from(doc.bucket).createSignedUrl(doc.file_path, 3600);
    if (data?.signedUrl) setPreviewUrl(data.signedUrl);
    setLoadingPreview(false);
  };

  // Auto-load preview for images
  useState(() => {
    if (isImage) loadPreview();
  });

  return (
    <div className="rounded-lg border bg-card p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{doc.file_name}</p>
          <p className="text-xs text-muted-foreground">
            {doc.sourceLabel} ·{" "}
            {format(new Date(doc.event?.occurred_at ?? doc.created_at), "MMM d, yyyy h:mm a")}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onView}>
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDownload}>
            <Download className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {isImage && previewUrl && (
        <ZoomableImage
          src={previewUrl}
          openUrl={previewUrl}
          fileName={doc.file_name}
          alt={doc.file_name}
          className="h-64 w-full rounded border bg-muted"
        />
      )}
    </div>
  );
}
